import React, { useEffect, useState, useRef } from "react";
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { THEME_COLOR, CLUB_NAME, LEVELS, LEVELS_MAP } from "../../config/club";
import { toPng } from 'html-to-image';
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";

export default function MatchHistoryModal({
    session,
    clubId,
    uid,
    onClose,
    isOwnProfile = false,
    passedStats = null, // New Prop
    passedRank = null   // New Prop
}) {
    const [stats, setStats] = useState(passedStats || { wins: 0, losses: 0 });
    const [rank, setRank] = useState(passedRank);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isTransparent, setIsTransparent] = useState(true);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [layoutMode, setLayoutMode] = useState('HERO');
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [showFullPreview, setShowFullPreview] = useState(false);
    const [showFooter, setShowFooter] = useState(true);
    const { user } = useAuth();
    const shareRef = useRef(null);
    // Auto-sync the preview whenever the layout or style changes
    // Sync preview only when the panel is open
    useEffect(() => {
        if (showShareOptions) {
            const syncPreview = async () => {
                if (!shareRef.current) return;
                setIsGenerating(true);
                try {
                    const dataUrl = await toPng(shareRef.current, {
                        cacheBust: true,
                        pixelRatio: 2,
                        backgroundColor: isTransparent ? null : '#000',
                    });
                    setPreviewUrl(dataUrl);
                } catch (err) {
                    console.error("Preview sync failed", err);
                } finally {
                    setIsGenerating(false);
                }
            };
            syncPreview();
        }
    }, [layoutMode, isTransparent, matches, showShareOptions, showFooter]);
    const copyToClipboard = async () => {
        if (!previewUrl) return;
        try {
            const response = await fetch(previewUrl);
            const blob = await response.blob();

            // We use the ClipboardItem API
            const data = [new ClipboardItem({ [blob.type]: blob })];
            await navigator.clipboard.write(data);

            toast.success("Copied! Open Instagram and 'Paste' or 'Add Sticker' to put this over your photo.");
        } catch (err) {
            console.error("Copy failed", err);
            toast.error("Clipboard copy not supported on this browser. Use 'Share' or 'Save' instead.");
        }
    };
    const CLUB_LOGO_URL = "/img/rrc.png";
    //console.log(user)
    // 1. Fetch Data
    useEffect(() => {
        const fetchMatchDetails = async () => {
            try {
                // Fetch ONLY the match history list
                const q = query(
                    collection(db, "clubs", clubId, "matchHistory"),
                    where("sessionId", "==", session.id),
                    where("playerIds", "array-contains", uid),
                    orderBy("playedAt", "desc")
                );
                const matchSnap = await getDocs(q);
                const userMatches = matchSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMatches(userMatches);

                // If for some reason stats weren't passed, calculate them locally
                if (!passedStats) {
                    const wins = userMatches.filter(m => {
                        const [a, b] = m.score.split("-").map(Number);
                        const onTeamA = m.players.slice(0, 2).some(p => p.id === uid);
                        return onTeamA ? a > b : b > a;
                    }).length;
                    setStats({ wins, losses: userMatches.length - wins });
                }
            } catch (err) {
                console.error("Error fetching matches:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMatchDetails();
    }, [session.id, clubId, uid, passedStats]);

    // 3. Share Logic
    const handleShare = async () => {
        if (!previewUrl) return;
        try {
            const blob = await (await fetch(previewUrl)).blob();
            const file = new File([blob], 'match-stats.png', { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: 'My Results' });
            } else {
                const link = document.createElement('a');
                link.download = 'stats.png';
                link.href = previewUrl;
                link.click();
            }
        } catch (err) {
            console.error("Share failed", err);
        }
    };

    return (
        <>
            <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                    <div
                        className="modal-content border-0 rounded-5 shadow-lg"
                        style={{
                            backgroundColor: "var(--card-bg)",
                            color: "var(--text-color)"
                        }}
                    >
                        <div className="modal-header border-0 pb-0 pt-4 px-4 d-block">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                {/* Modern Date & Status Badge */}
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="px-3 py-1 rounded-pill"
                                        style={{
                                            background: `${THEME_COLOR}10`,
                                            backdropFilter: 'blur(10px)',
                                            border: `1px solid ${THEME_COLOR}20`,
                                        }}
                                    >
                                        <span style={{
                                            color: THEME_COLOR,
                                            fontSize: '0.65rem',
                                            fontWeight: '900',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1.5px'
                                        }}>
                                            {session.endedAt?.toDate
                                                ? new Date(session.endedAt.toDate()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                : 'Live Session'}
                                        </span>
                                    </div>

                                    {rank && (
                                        <div
                                            className="px-3 py-1 rounded-pill"
                                            style={{
                                                background: THEME_COLOR,
                                                border: '1px solid #334155'
                                            }}
                                        >
                                            <span style={{
                                                color: '#F8FAFC',
                                                fontSize: '0.65rem',
                                                fontWeight: '800',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px'
                                            }}>
                                                Rank #{rank}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Minimalist Close Button */}
                                <button
                                    type="button"
                                    className="btn-close shadow-none opacity-50"
                                    onClick={onClose}
                                    style={{ fontSize: '0.8rem' }}
                                ></button>
                            </div>

                            {/* Typography Stack for Long Names */}
                            <div className="pe-4">
                                <h3 className="fw-black mb-1" style={{
                                    color: "var(--text-color)",
                                    fontSize: '1.75rem',
                                    lineHeight: '1.1',
                                    letterSpacing: '-0.04em',
                                    wordBreak: 'break-word'
                                }}>
                                    {session.name}
                                </h3>

                                <div className="d-flex align-items-center gap-2 mt-2">
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '3px',
                                            backgroundColor: rank === 1 ? '#FFD700' : rank <= 3 ? '#94A3B8' : THEME_COLOR
                                        }}
                                    />
                                    <span className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '2px' }}>
                                        {rank === 1 ? 'Top Seed' : rank <= 3 ? 'Elite Tier' : rank <= 10 ? 'High Flyer' : 'Contender'}
                                    </span>
                                </div>
                            </div>

                            {/* Subtle Decorative Line */}
                            <div className="mt-4" style={{
                                height: '1px',
                                width: '100%',
                                background: 'linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%)'
                            }} />
                        </div>

                        <div className="modal-body p-4">
                            {/* Session Stats Header */}
                            {!loading && matches.length > 0 && (
                                <div className="mb-4">

                                    <div
                                        className="d-flex align-items-stretch shadow-sm rounded-4 overflow-hidden"
                                        style={{
                                            border: "1px solid var(--border-color)",
                                            backgroundColor: "var(--card-bg)"
                                        }}
                                    >
                                        {/* Wins Section */}
                                        <div
                                            className="flex-grow-1 p-3 text-center"
                                            style={{ borderRight: "1px solid var(--border-color)" }}
                                        >
                                            <div
                                                className="text-uppercase fw-black text-success tracking-widest mb-1"
                                                style={{ fontSize: '0.65rem' }}
                                            >
                                                Wins
                                            </div>
                                            <div
                                                className="h3 mb-0 fw-black"
                                                style={{ color: "var(--text-color)" }}
                                            >
                                                {stats.wins}
                                            </div>
                                        </div>

                                        {/* Win Rate Section (Center) */}
                                        <div
                                            className="d-flex align-items-center justify-content-center px-3"
                                            style={{ backgroundColor: "var(--surface-bg)" }}
                                        >
                                            <div
                                                className="rounded-circle shadow-sm d-flex align-items-center justify-content-center fw-bold"
                                                style={{
                                                    width: '45px',
                                                    height: '45px',
                                                    fontSize: '0.75rem',
                                                    color: THEME_COLOR,
                                                    border: `2px solid ${THEME_COLOR}30`,
                                                    backgroundColor: "var(--card-bg)"
                                                }}
                                            >
                                                {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                            </div>
                                        </div>

                                        {/* Losses Section */}
                                        <div
                                            className="flex-grow-1 p-3 text-center"
                                            style={{ borderLeft: "1px solid var(--border-color)" }}
                                        >
                                            <div
                                                className="text-uppercase fw-black text-danger tracking-widest mb-1"
                                                style={{ fontSize: '0.65rem' }}
                                            >
                                                Losses
                                            </div>
                                            <div
                                                className="h3 mb-0 fw-black"
                                                style={{ color: "var(--text-color)" }}
                                            >
                                                {stats.losses}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sub-label */}
                                    <div className="text-center mt-2">
                                        <span
                                            className="fw-normal rounded-pill px-3 py-2"
                                            style={{
                                                fontSize: '0.7rem',
                                                backgroundColor: "var(--surface-bg)",
                                                color: "var(--muted-text)",
                                                border: "1px solid var(--border-color)"
                                            }}
                                        >
                                            <i className="bi bi-info-circle me-1"></i>
                                            Session performance based on {matches.length} matches
                                        </span>
                                    </div>

                                </div>
                            )}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border spinner-border-sm" style={{ color: THEME_COLOR }} />
                                </div>
                            ) : matches.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-muted small">No matches found for you in this session.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {matches.map((m) => {
                                        const [a, b] = m.score.split("-").map(Number);
                                        const onTeamA = m.players.slice(0, 2).some(p => p.id === uid);
                                        const isWin = onTeamA ? a > b : b > a;

                                        return (
                                            <div
                                                key={m.id}
                                                className="p-3 rounded-4 shadow-sm mb-3"
                                                style={{
                                                    backgroundColor: "var(--card-bg)",
                                                    border: "1px solid var(--border-color)"
                                                }}
                                            >
                                                {/* Top Status Row */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <span
                                                        className={`badge rounded-pill px-3 ${isWin ? "bg-success" : "bg-danger"}`}
                                                        style={{ fontSize: "0.6rem" }}
                                                    >
                                                        {isWin ? "WIN" : "LOSS"}
                                                    </span>

                                                    <span
                                                        className="fw-bold"
                                                        style={{
                                                            fontSize: "0.65rem",
                                                            color: "var(--muted-text)"
                                                        }}
                                                    >
                                                        {m.playedAt?.toDate().toLocaleTimeString([], {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </span>
                                                </div>

                                                {/* Scoreboard Table */}
                                                <table className="w-100 text-center" style={{ tableLayout: "fixed" }}>
                                                    <tbody>
                                                        <tr>
                                                            <td
                                                                className={`text-truncate p-1 ${onTeamA && m.players[0].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        onTeamA && m.players[0].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[0]?.name}
                                                            </td>

                                                            <td rowSpan="3" className="align-middle px-2">
                                                                <div
                                                                    className="fw-black py-2 rounded-3"
                                                                    style={{
                                                                        fontSize: "1.4rem",
                                                                        color: THEME_COLOR,
                                                                        backgroundColor: "var(--surface-bg)",
                                                                    }}
                                                                >
                                                                    {m.score}
                                                                </div>
                                                            </td>

                                                            <td
                                                                className={`text-truncate p-1 ${!onTeamA && m.players[2].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        !onTeamA && m.players[2].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[2]?.name}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td
                                                                className="small"
                                                                style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}
                                                            >
                                                                &
                                                            </td>
                                                            <td
                                                                className="small"
                                                                style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}
                                                            >
                                                                &
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td
                                                                className={`text-truncate p-1 ${onTeamA && m.players[1].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        onTeamA && m.players[1].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[1]?.name}
                                                            </td>

                                                            <td
                                                                className={`text-truncate p-1 ${!onTeamA && m.players[3].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        !onTeamA && m.players[3].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[3]?.name}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* --- COLLAPSIBLE SHARING PANEL --- */}
                        {isOwnProfile && (
                            <div
                                style={{
                                    position: "relative",
                                    zIndex: 1100,
                                    backgroundColor: "var(--card-bg)",
                                    borderTop: "1px solid var(--border-color)",
                                    boxShadow: "0 -4px 12px rgba(0,0,0,0.15)"
                                }}
                            >
                                {/* Pull Tab / Toggle Button */}
                                <button
                                    onClick={() => setShowShareOptions(!showShareOptions)}
                                    className="btn w-100 rounded-0 border-0 py-2 d-flex align-items-center justify-content-center gap-2"
                                    style={{
                                        backgroundColor: "var(--surface-bg)",
                                        color: "var(--text-color)"
                                    }}
                                >
                                    <i className={`bi bi-chevron-${showShareOptions ? "down" : "up"}`}></i>
                                    <span className="fw-bold small text-uppercase">
                                        {showShareOptions ? "Hide Sharing Options" : "Share My Results"}
                                    </span>
                                </button>

                                <div
                                    className="overflow-hidden"
                                    style={{
                                        maxHeight: showShareOptions ? "600px" : "0px",
                                        transition: "max-height 0.4s ease-in-out",
                                        backgroundColor: "var(--surface-bg)"
                                    }}
                                >
                                    <div className="p-4 pt-2">

                                        {/* Layout Selector */}
                                        <div className="mb-4">
                                            <label
                                                className="small fw-bold text-uppercase mb-2 d-block"
                                                style={{
                                                    fontSize: "0.65rem",
                                                    color: "var(--muted-text)"
                                                }}
                                            >
                                                Card Layout
                                            </label>

                                            <div
                                                className="d-flex p-1 rounded-pill"
                                                style={{
                                                    backgroundColor: "var(--card-bg)",
                                                    border: "1px solid var(--border-color)"
                                                }}
                                            >
                                                {["HERO", "COMPACT", "LIST", "VIBE", "PASS"].map((m) => (
                                                    <button
                                                        key={m}
                                                        onClick={() => setLayoutMode(m)}
                                                        className="flex-fill btn btn-sm border-0 rounded-pill py-2"
                                                        style={{
                                                            backgroundColor:
                                                                layoutMode === m ? THEME_COLOR : "transparent",
                                                            color:
                                                                layoutMode === m
                                                                    ? "#fff"
                                                                    : "var(--muted-text)",
                                                            transition: "0.2s"
                                                        }}
                                                    >
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div
                                            className="d-flex align-items-center justify-content-between p-3 rounded-4"
                                            style={{
                                                backgroundColor: "var(--card-bg)",
                                                border: "1px solid var(--border-color)",
                                                gap: "20px"
                                            }}
                                        >
                                            {/* LEFT SIDE: Transparent Mode */}
                                            <div className="d-flex align-items-center justify-content-between flex-grow-1">
                                                <div style={{ marginRight: '10px' }}>
                                                    <div className="fw-bold small" style={{ color: "var(--text-color)", lineHeight: 1.2 }}>
                                                        Transparent
                                                    </div>
                                                    <div style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}>
                                                        Sticker Mode
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => setIsTransparent(!isTransparent)}
                                                    style={{
                                                        width: "40px",
                                                        height: "22px",
                                                        backgroundColor: isTransparent ? THEME_COLOR : "var(--border-color)",
                                                        borderRadius: "50px",
                                                        cursor: "pointer",
                                                        transition: "0.3s",
                                                        position: "relative",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <div style={{
                                                        position: "absolute",
                                                        top: "2px",
                                                        left: isTransparent ? "20px" : "2px",
                                                        width: "18px",
                                                        height: "18px",
                                                        backgroundColor: "var(--card-bg)",
                                                        borderRadius: "50%",
                                                        transition: "0.3s"
                                                    }} />
                                                </div>
                                            </div>

                                            {/* MIDDLE DIVIDER */}
                                            <div style={{ width: '1px', height: '30px', backgroundColor: 'var(--border-color)' }} />

                                            {/* RIGHT SIDE: Footer Toggle */}
                                            <div className="d-flex align-items-center justify-content-between flex-grow-1">
                                                <div style={{ marginRight: '10px' }}>
                                                    <div className="fw-bold small" style={{ color: "var(--text-color)", lineHeight: 1.2 }}>
                                                        Club Badge
                                                    </div>
                                                    <div style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}>
                                                        Show Footer
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => setShowFooter(!showFooter)}
                                                    style={{
                                                        width: "40px",
                                                        height: "22px",
                                                        backgroundColor: showFooter ? THEME_COLOR : "var(--border-color)",
                                                        borderRadius: "50px",
                                                        cursor: "pointer",
                                                        transition: "0.3s",
                                                        position: "relative",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <div style={{
                                                        position: "absolute",
                                                        top: "2px",
                                                        left: showFooter ? "20px" : "2px",
                                                        width: "18px",
                                                        height: "18px",
                                                        backgroundColor: "var(--card-bg)",
                                                        borderRadius: "50%",
                                                        transition: "0.3s"
                                                    }} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Preview */}
                                        <div className="text-center mb-3 position-relative mt-4">
                                            {isGenerating && (
                                                <div
                                                    className="position-absolute top-50 start-50 translate-middle spinner-border"
                                                    style={{ color: THEME_COLOR, zIndex: 1 }}
                                                />
                                            )}

                                            <img
                                                src={previewUrl}
                                                onClick={() => !isGenerating && setShowFullPreview(true)}
                                                className="img-fluid rounded-4 shadow-sm"
                                                style={{
                                                    maxHeight: "200px",
                                                    cursor: isGenerating ? "default" : "zoom-in",
                                                    opacity: isGenerating ? 0.5 : 1,
                                                    transition: "opacity 0.2s",
                                                    border: "1px solid var(--border-color)"
                                                }}
                                                alt="Profile Preview"
                                            />

                                            {!isGenerating && previewUrl && (
                                                <div
                                                    className="small mt-1"
                                                    style={{ color: "var(--muted-text)" }}
                                                >
                                                    <i className="bi bi-search me-1"></i>
                                                    Tap to enlarge
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="d-grid gap-2">
                                            <button
                                                className="btn rounded-pill fw-bold py-2"
                                                style={{
                                                    backgroundColor: THEME_COLOR,
                                                    color: "#fff"
                                                }}
                                                onClick={handleShare}
                                            >
                                                <i className="bi bi-send-fill me-2"></i>
                                                Share / Download
                                            </button>

                                            {isTransparent && (
                                                <button
                                                    className="btn rounded-pill fw-bold py-2"
                                                    style={{
                                                        backgroundColor: "transparent",
                                                        border: `1px solid ${THEME_COLOR}`,
                                                        color: THEME_COLOR
                                                    }}
                                                    onClick={copyToClipboard}
                                                >
                                                    <i className="bi bi-clipboard-plus me-2"></i>
                                                    Copy as Sticker
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* --- PREMIUM HIDDEN CANVAS ---  */}
            <div style={{ position: 'absolute', left: '-9999px', top: '0' }}>
                <div ref={shareRef} style={{
                    width: '1080px', height: '1920px',
                    backgroundColor: isTransparent ? 'transparent' : '#050505', // Deep rich black
                    padding: '120px 80px', display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between', alignItems: 'center',
                    fontFamily: "'Inter', 'Helvetica Neue', sans-serif"
                }}>

                    {/* Choice 1: THE HERO (Refined & Dramatic) */}
                    {layoutMode === 'HERO' && (
                        <div style={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            justifyContent: 'center', alignItems: 'center', width: '100%',
                            // This shadow creates a "rim" that protects white text on light photos
                            filter: isTransparent ? 'drop-shadow(0 0 30px rgba(0,0,0,0.8))' : 'none'
                        }}>
                            <img src={CLUB_LOGO_URL} style={{
                                width: '220px', marginBottom: '60px',
                                filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))'
                            }} />

                            <div style={{ position: 'relative', marginBottom: '40px' }}>
                                <div style={{
                                    fontSize: '18rem', fontWeight: '900', color: '#fff',
                                    lineHeight: '0.8', letterSpacing: '-8px', fontStyle: 'italic',
                                    // Secondary text stroke for maximum legibility
                                    WebkitTextStroke: isTransparent ? '2px rgba(0,0,0,0.3)' : 'none'
                                }}>
                                    {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                </div>
                                {rank && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-40px',
                                        left: '-20px', // Put rank on the top-left, WR on top-right
                                        backgroundColor: '#fff',
                                        padding: '10px 25px',
                                        borderRadius: '10px',
                                        color: '#000',
                                        fontSize: '2rem',
                                        fontWeight: '900',
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.3)'
                                    }}>
                                        #{rank}
                                    </div>
                                )}
                                <div style={{
                                    position: 'absolute', top: '-40px', right: '-20px', backgroundColor: THEME_COLOR,
                                    padding: '10px 30px', borderRadius: '10px', color: '#000', fontSize: '2.5rem', fontWeight: '800'
                                }}>
                                    WR
                                </div>
                            </div>


                            <div style={{ fontSize: '2rem', color: '#666', letterSpacing: '10px', textTransform: 'uppercase', marginBottom: '80px' }}>
                                {session.name}
                            </div>

                            <div style={{
                                display: 'flex', gap: '100px', background: 'rgba(255,255,255,0.03)',
                                padding: '40px 100px', borderRadius: '40px', border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', color: '#4ade80', fontWeight: 'bold', opacity: 0.8 }}>WINS</div>
                                    <div style={{ fontSize: '6rem', color: '#fff', fontWeight: '900' }}>{stats.wins}</div>
                                </div>
                                <div style={{ width: '1px', background: 'rgba(255,255,255,0.2)' }}></div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', color: '#f87171', fontWeight: 'bold', opacity: 0.8 }}>LOSSES</div>
                                    <div style={{ fontSize: '6rem', color: '#fff', fontWeight: '900' }}>{stats.losses}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Choice 2: THE COMPACT PILL (Sleek Watermark) */}
                    {layoutMode === 'COMPACT' && (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <div style={{
                                // 1. WEBKIT PREFIX IS MANDATORY FOR IOS
                                WebkitBackdropFilter: 'blur(40px)',
                                backdropFilter: 'blur(40px)',

                                // 2. PREVENT PIXEL BLEEDING
                                isolation: 'isolate',
                                WebkitMaskImage: '-webkit-radial-gradient(white, black)', // Fixes border-radius overflow on iOS

                                // 3. BACKGROUND ADJUSTMENT
                                // Using hex with alpha or rgba is fine, but keep it consistent
                                background: isTransparent ? 'rgba(0, 0, 0, 0.4)' : 'rgba(15, 15, 15, 0.85)',

                                border: '1px solid rgba(255,255,255,0.15)',
                                padding: '40px 60px',
                                borderRadius: '120px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '40px',
                                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',

                                // 4. FORCE HARDWARE ACCELERATION
                                transform: 'translateZ(0)',
                            }}>
                                <img src={CLUB_LOGO_URL} style={{ width: '110px' }} />
                                <div style={{ height: '70px', width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ color: '#aaa', fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '2px' }}>RECORD</span>
                                    <span style={{ color: '#fff', fontSize: '3.5rem', fontWeight: '900' }}>{stats.wins}W - {stats.losses}L</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ color: '#aaa', fontSize: '1.2rem', fontWeight: 'bold', letterSpacing: '2px' }}>RANK</span>
                                    <span style={{ color: THEME_COLOR, fontSize: '3rem', fontWeight: '900' }}>#{rank || '--'}</span>
                                </div>
                                <div style={{ height: '70px', width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                <div style={{
                                    background: THEME_COLOR, width: '140px', height: '140px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#ffffff', fontSize: '3rem', fontWeight: '900', marginLeft: '20px'
                                }}>
                                    {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CHOICE 3: THE GRIND (Match History - Ultra-Minimal Elite) */}
                    {layoutMode === 'LIST' && (
                        <div style={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            justifyContent: 'center', width: '940px',
                            position: 'relative'
                        }}>
                            {/* LOGO WATERMARK: Placed behind the header for depth */}
                            {/* <img
                                src={CLUB_LOGO_URL}
                                alt=""
                                style={{
                                    position: 'absolute', top: '-20px', right: '0',
                                    height: '180px', opacity: 0.1, pointerEvents: 'none',
                                    filter: 'grayscale(1) brightness(2)'
                                }}
                            /> */}

                            {/* HEADER: Clean, Thin, and Sophisticated */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', marginBottom: '40px',
                                padding: '0 10px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
                                    <img src={CLUB_LOGO_URL} style={{ height: '50px', width: '50px', objectFit: 'contain' }} alt="logo" />
                                    <div style={{ width: '2px', height: '40px', background: 'rgba(255,255,255,0.2)' }} />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: '800', letterSpacing: '6px', opacity: 0.5 }}>MATCHES</span>
                                        <span style={{ color: '#fff', fontSize: '2.0rem', fontWeight: '200', letterSpacing: '-1px', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>{session.name} </span>
                                    </div>
                                </div>

                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: '#fff', fontSize: '3rem', fontWeight: '900', letterSpacing: '-2px' }}>
                                        {stats.wins}<span style={{ color: THEME_COLOR }}>W</span> — {stats.losses}<span style={{ opacity: 0.5 }}>L</span>
                                    </div>
                                    <div style={{ color: THEME_COLOR, fontSize: '0.8rem', fontWeight: '900', letterSpacing: '3px' }}>WIN RATE: {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%</div>
                                </div>
                            </div>

                            {/* MATCH LIST: Clean lines, no bulky boxes */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                                {matches.slice(0, 6).map((m, idx) => {
                                    const [a, b] = m.score.split("-").map(Number);

                                    const userOnA = m.players.slice(0, 2).some(p => p.id === uid);

                                    const teamAplayers = userOnA ? m.players.slice(0, 2) : m.players.slice(2, 4);
                                    const teamBplayers = userOnA ? m.players.slice(2, 4) : m.players.slice(0, 2);

                                    const userScore = userOnA ? a : b;
                                    const oppScore = userOnA ? b : a;
                                    const isWin = userScore > oppScore;

                                    return (
                                        <div key={idx} style={{
                                            display: 'flex', alignItems: 'center',
                                            padding: '18px 30px',
                                            background: isTransparent ? 'rgba(10, 10, 10, 0.6)' : 'rgba(255,255,255,0.02)',
                                            backdropFilter: 'blur(15px)',
                                            transition: '0.2s'
                                        }}>
                                            {/* Status Indicator */}
                                            <div style={{
                                                width: '4px', height: '30px',
                                                background: isWin ? '#4ade80' : '#f87171',
                                                marginRight: '30px',
                                                boxShadow: `0 0 15px ${isWin ? '#4ade80' : '#f87171'}66`
                                            }} />

                                            {/* Players */}
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                {/* User Team (first) */}
                                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
                                                    <span style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700' }}>{teamAplayers[0].name}</span>
                                                    <span style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', opacity: 0.8 }}>{teamAplayers[1].name}</span>
                                                </div>

                                                <span style={{ color: THEME_COLOR, fontWeight: '900', fontSize: '0.8rem', opacity: 0.5 }}>VS</span>

                                                {/* Opponent Team */}
                                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
                                                    <span style={{ color: '#fff', fontSize: '1rem', fontWeight: '400', opacity: 0.6 }}>{teamBplayers[0].name}</span>
                                                    <span style={{ color: '#fff', fontSize: '1rem', fontWeight: '400', opacity: 0.6 }}>{teamBplayers[1].name}</span>
                                                </div>
                                            </div>

                                            {/* Score Section */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                <div style={{
                                                    fontSize: '1.8rem', fontWeight: '900', color: '#fff',
                                                    fontFamily: 'monospace', letterSpacing: '2px',
                                                    background: 'rgba(255,255,255,0.05)',
                                                    padding: '5px 15px', borderRadius: '6px'
                                                }}>
                                                    {userOnA ? m.score : `${b}-${a}`}
                                                </div>

                                                <div style={{
                                                    width: '80px', textAlign: 'center',
                                                    fontSize: '0.7rem', fontWeight: '900', letterSpacing: '1px',
                                                    color: isWin ? '#4ade80' : '#f87171',
                                                    textTransform: 'uppercase',
                                                    padding: '4px', border: `1px solid ${isWin ? '#4ade8055' : '#f8717155'}`,
                                                    borderRadius: '4px'
                                                }}>
                                                    {isWin ? 'Victory' : 'Defeat'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* CHOICE 4: THE VIBE (Ultra-Premium Watermark) */}
                    {layoutMode === 'VIBE' && (
                        <div style={{
                            flex: 1,
                            width: '100%',
                            height: '100%',
                            // Changed to semi-transparent black for the sticker look
                            backgroundColor: isTransparent ? 'rgba(0, 0, 0, 0.7)' : '#000',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            fontFamily: '"Syncopate", sans-serif',
                            borderRadius: '32px', // Added radius to make it feel like a sticker
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            {/* 1. VERTICAL SIDEBAR (Session Info) */}
                            <div style={{
                                position: 'absolute',
                                left: '20px',
                                top: '20px',
                                bottom: '20px',
                                width: '40px',
                                borderLeft: `1px solid ${THEME_COLOR}`,
                                zIndex: 5,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                paddingLeft: '10px'
                            }}>
                                <div style={{
                                    transform: 'rotate(-90deg) translateX(0)',
                                    transformOrigin: 'left bottom',
                                    whiteSpace: 'nowrap',
                                    color: THEME_COLOR,
                                    fontSize: '0.7rem',
                                    fontWeight: '900',
                                    letterSpacing: '5px',
                                    textTransform: 'uppercase'
                                }}>
                                    {session.name || 'RRC OFFICIAL'} — {new Date().toLocaleDateString()}
                                </div>
                            </div>

                            {/* 2. MAIN CONTENT AREA */}
                            <div style={{
                                marginTop: 'auto',
                                padding: '60px 40px',
                                zIndex: 10,
                                position: 'relative'
                            }}>
                                {/* BIG NAME BACKGROUND TEXT */}
                                <div style={{
                                    position: 'absolute',
                                    top: '-40px',
                                    left: '40px',
                                    fontSize: '8rem',
                                    fontWeight: '900',
                                    color: 'transparent',
                                    WebkitTextStroke: '1px rgba(255,255,255,0.1)',
                                    zIndex: -1,
                                    lineHeight: 1
                                }}>
                                    {user.displayName.split(' ')[0].toUpperCase()}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            backgroundColor: THEME_COLOR,
                                            color: '#ffffff',
                                            display: 'inline-block',
                                            padding: '4px 12px',
                                            fontSize: '0.8rem',
                                            fontWeight: '900',
                                            marginBottom: '10px'
                                        }}>
                                            {LEVELS_MAP[user?.level] || 'PLAYER'}
                                        </div>
                                        <h1 style={{
                                            color: '#fff',
                                            fontSize: '4.5rem',
                                            margin: 0,
                                            lineHeight: 0.9,
                                            fontWeight: '900',
                                            letterSpacing: '-2px'
                                        }}>
                                            {user.displayName.toUpperCase()}
                                        </h1>
                                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem', marginTop: '5px' }}>
                                            @{user.username || 'racketriot'}
                                        </div>
                                    </div>

                                    {/* LOGO */}
                                    <img src={CLUB_LOGO_URL} style={{ width: '180px', height: '180px', objectFit: 'contain' }} />
                                </div>

                                {/* STATS STRIP */}
                                <div style={{
                                    display: 'flex',
                                    marginTop: '40px',
                                    gap: '40px',
                                    borderTop: '1px solid rgba(255,255,255,0.2)',
                                    paddingTop: '20px'
                                }}>
                                    <div>
                                        <div style={{ color: THEME_COLOR, fontSize: '0.7rem', fontWeight: '900' }}>WINS</div>
                                        <div style={{ color: '#fff', fontSize: '2.5rem', fontWeight: '900' }}>{stats.wins}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: '900' }}>LOSSES</div>
                                        <div style={{ color: '#fff', fontSize: '2.5rem', fontWeight: '900' }}>{stats.losses}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: '900' }}>RATIO</div>
                                        <div style={{ color: '#fff', fontSize: '2.5rem', fontWeight: '900' }}>
                                            {((stats.wins / (stats.wins + stats.losses || 1)) * 100).toFixed(0)}%
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 3. DECORATIVE CORNER ELEMENT */}
                            <div style={{
                                position: 'absolute',
                                top: '40px',
                                right: '40px',
                                textAlign: 'right',
                                zIndex: 10
                            }}>
                                <div style={{ color: '#fff', fontSize: '0.6rem', letterSpacing: '3px', opacity: 0.6 }}>VERIFIED PLAYER</div>
                                <div style={{ width: '40px', height: '2px', background: THEME_COLOR, marginLeft: 'auto', marginTop: '5px' }} />
                            </div>
                        </div>
                    )}

                    {layoutMode === 'PASS' && (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <div style={{
                                WebkitBackdropFilter: 'blur(30px)',
                                backdropFilter: 'blur(30px)',
                                isolation: 'isolate',
                                //WebkitMaskImage: '-webkit-radial-gradient(white, black)',
                                background: isTransparent ? 'rgba(0, 0, 0, 0.4)' : 'rgba(15, 15, 15, 0.95)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                padding: '30px 40px',
                                borderRadius: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                boxShadow: '0 25px 60px -15px rgba(0,0,0,0.6)',
                                transform: 'translateZ(0)',
                                position: 'relative',
                                minWidth: '620px', // Slightly wider to accommodate longer session names
                                /* ADD THESE THREE LINES */
                                WebkitMaskImage: 'radial-gradient(circle at 250px 0px, transparent 12px, black 13px), radial-gradient(circle at 250px 100%, transparent 12px, black 13px)',
                                WebkitMaskComposite: 'source-in',
                                maskComposite: 'intersect',

                            }}>

                                {/* LEFT SECTION: Branding & Name */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '25px',
                                    paddingRight: '35px',
                                    borderRight: '2px dashed rgba(255,255,255,0.2)'
                                }}>

                                    <img
                                        src={CLUB_LOGO_URL}
                                        style={{ width: '105px', filter: 'brightness(1.2)' }}
                                    />
                                    <div>

                                        <div style={{ color: THEME_COLOR, fontSize: '0.7rem', fontWeight: '900', letterSpacing: '4px', textTransform: 'uppercase' }}>
                                            Session Pass
                                        </div>

                                        <div style={{ color: '#fff', fontSize: '2.4rem', fontWeight: '900', letterSpacing: '-1px' }}>
                                            {user.displayName}
                                        </div>

                                        {/* NEW: Session Name Display */}
                                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', fontWeight: '700', marginTop: '2px' }}>
                                            {session.name || CLUB_NAME}
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT SECTION: Profile Hint & Username */}
                                <div style={{ paddingLeft: '35px', display: 'flex', alignItems: 'center', gap: '20px', flexGrow: 1 }}>
                                    {/* Profile Hint Avatar */}
                                    <div style={{ position: 'relative' }}>
                                        <img
                                            src={user?.photoURL || CLUB_LOGO_URL}
                                            crossOrigin="anonymous"
                                            style={{
                                                width: '55px',
                                                height: '55px',
                                                borderRadius: '15px',
                                                objectFit: 'cover',
                                                border: `1px solid rgba(255,255,255,0.2)`
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute', bottom: '-4px', right: '-4px',
                                            background: THEME_COLOR, width: '14px', height: '14px',
                                            borderRadius: '50%', border: '2px solid #000'
                                        }} />
                                    </div>

                                    <div style={{ textAlign: 'left' }}>
                                        {/* NEW: Username instead of ID */}
                                        <div style={{
                                            color: THEME_COLOR,
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                            fontWeight: 'bold'
                                        }}>
                                            @{user.username || user.displayName.toLowerCase().replace(/\s/g, '')}
                                        </div>
                                        <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: '900', marginTop: '2px' }}>
                                            {LEVELS_MAP[user?.level] || 'Player'}
                                        </div>
                                        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '1px' }}>
                                            {new Date().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    </div>

                                    {/* Decorative Branding */}
                                    {/* THE VERIFIED CHECK (Compact & HD) */}
                                    <div style={{
                                        marginLeft: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '80px' // Ensures it doesn't get squashed
                                    }}>
                                        <div style={{
                                            width: '54px',
                                            height: '54px',
                                            borderRadius: '50%',
                                            background: `${THEME_COLOR}15`, // Very subtle theme tint
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: `1.5px solid ${THEME_COLOR}55`,
                                            position: 'relative',
                                            boxShadow: `0 0 20px ${THEME_COLOR}10`
                                        }}>
                                            {/* Inner glow for depth */}
                                            <div style={{
                                                position: 'absolute',
                                                inset: '4px',
                                                borderRadius: '50%',
                                                border: `1px solid ${THEME_COLOR}22`
                                            }} />

                                            {/* Custom SVG Checkmark (Reliable for HD Capture) */}
                                            <svg
                                                width="26"
                                                height="26"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M5 13L9 17L19 7"
                                                    stroke={THEME_COLOR}
                                                    strokeWidth="3.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>

                                        <div style={{
                                            marginTop: '8px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{
                                                color: THEME_COLOR,
                                                fontSize: '0.6rem',
                                                fontWeight: '900',
                                                letterSpacing: '2px',
                                                textTransform: 'uppercase'
                                            }}>
                                                Verified
                                            </div>
                                            <div style={{
                                                color: 'rgba(255,255,255,0.3)',
                                                fontSize: '0.5rem',
                                                fontWeight: 'bold',
                                                letterSpacing: '1px'
                                            }}>
                                                PLAYER
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- ENHANCED PREMIUM BRAND FOOTER --- */}
                    {showFooter && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        marginTop: '50px',
                        padding: '10px 0',
                        position: 'relative',
                        zIndex: 100
                    }}>
                        {/* 1. CLEAN DIVIDER: No glow, just high-contrast visibility */}
                        {/* <div style={{
        height: '1px',
        width: '240px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        position: 'relative'
    }}>
        <div style={{
            position: 'absolute',
            top: '-1px', left: '50%', transform: 'translateX(-50%)',
            width: '40px', height: '3px', 
            backgroundColor: THEME_COLOR,
            borderRadius: '2px'
        }} />
    </div> */}

                        {/* 2. THE CLEAN PILL: Sharpened borders, no bleeding shadows */}
                        <div style={{
                            backgroundColor: 'rgba(10, 10, 10, 0.85)', // Slightly more solid to stop bleed
                            backdropFilter: 'blur(15px)',
                            WebkitBackdropFilter: 'blur(15px)',

                            padding: '10px 28px',
                            borderRadius: '50px', // Perfect rounded pill
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',

                            // SHARP BORDER: Inner light stroke + Thin dark outer ring for definition
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
                        }}>
                            {/* ICON: Cleaned up shadow */}
                            <div style={{
                                fontSize: '1.8rem',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                🦝
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{
                                    color: '#FFFFFF',
                                    fontSize: '1.3rem',
                                    fontWeight: '900',
                                    letterSpacing: '5px',
                                    textTransform: 'uppercase',
                                    lineHeight: '1.1'
                                }}>
                                    RACKET <span style={{ color: THEME_COLOR, fontWeight: '300' }}>RIOT</span> CLUB
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginTop: '2px'
                                }}>
                                    <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.15)' }} />
                                    <div style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '0.6rem',
                                        fontWeight: '700',
                                        letterSpacing: '4px',
                                        textTransform: 'uppercase'
                                    }}>
                                        EST. 2024
                                    </div>
                                    <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.15)' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </div>
            {/* FULL SCREEN OVERLAY FOR GENERATED IMAGE */}
            {showFullPreview && (
                <div
                    className="preview-overlay"
                    style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)'
                    }}
                    onClick={() => setShowFullPreview(false)}
                >
                    <div className="position-relative text-center p-3">
                        {/* Close Button */}
                        <button
                            className="btn btn-light rounded-circle position-absolute top-0 end-0 m-2 shadow"
                            onClick={() => setShowFullPreview(false)}
                            style={{ zIndex: 10001, width: '40px', height: '40px' }}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>

                        <img
                            src={previewUrl}
                            className="img-fluid rounded shadow-lg"
                            style={{ maxHeight: '85vh', maxWidth: '95vw', border: '2px solid rgba(255,255,255,0.2)' }}
                            onClick={(e) => e.stopPropagation()} // Prevents closing when clicking the image itself
                        />

                        <div className="mt-3 text-white-50 small">
                            Long press to save or use the share buttons below
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}